import { Skeleton, Stack } from '@mui/material';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist/types/src/display/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - No types available for pdfjs-dist
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

// react-pdf v5.x ships pdfjs-dist 2.x — use the matching .js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type Props = {
    file: string | Blob | File;
    onClickCoordinates?: (value: { x: number; y: number; pageNumber: number }) => void;
};

const PDFViewer = ({ file, onClickCoordinates }: Props) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [pdfWidth, setPdfWidth] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return undefined;

        const updateWidth = () => {
            const nextWidth = node.clientWidth;
            if (nextWidth > 0) {
                setPdfWidth((current) => (current === nextWidth ? current : nextWidth));
            }
        };

        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const onDocumentLoadSuccess = useCallback((pdf: PDFDocumentProxy) => {
        setNumPages(pdf.numPages);
        setError(null);
    }, []);

    const handleDocumentLoadError = useCallback(() => {
        setError('Failed to load PDF.');
    }, []);

    const handlePageClick = useCallback(
        (event: React.MouseEvent, page: PDFPageProxy) => {
            if (!onClickCoordinates) return;

            event.stopPropagation();

            // Skip if clicking on text spans
            if (event.target instanceof HTMLElement && event.target.tagName === 'SPAN') {
                return;
            }

            const canvas = event.target as HTMLCanvasElement;
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            const { width, height } = rect;

            onClickCoordinates({
                x: width / x,
                y: height / y,
                pageNumber: page.pageNumber,
            });
        },
        [onClickCoordinates]
    );

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            {error ? (
                <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>{error}</div>
            ) : (
                <Document
                    file={file}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={handleDocumentLoadError}
                    options={{
                        standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts`,
                    }}
                    loading={
                        <Stack sx={{ p: 2 }}>
                            <Stack spacing={2}>
                                {[1, 2, 3].map((index) => (
                                    <Skeleton
                                        key={index}
                                        variant="rectangular"
                                        width="100%"
                                        height={80}
                                        animation="wave"
                                        sx={{
                                            bgcolor: 'grey.100',
                                            borderRadius: 1,
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    }
                >
                    {Array.from({ length: numPages }, (_, index) => index + 1).map((pageNum) => (
                        <div key={pageNum} style={{ marginBottom: '10px' }}>
                            <Page
                                pageNumber={pageNum}
                                width={pdfWidth || undefined}
                                onClick={handlePageClick}
                                renderTextLayer={false}
                                renderAnnotationLayer={true}
                                loading={
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height={80}
                                        animation="wave"
                                        sx={{ bgcolor: 'grey.100', borderRadius: 1 }}
                                    />
                                }
                            />
                        </div>
                    ))}
                </Document>
            )}
        </div>
    );
};

export default PDFViewer;
